// packages/web/src/components/layout/providers/AuthProvider.tsx
// FAANG-GRADE: Centralized authentication state management
// Separates auth logic from UI rendering for better testability and maintainability

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { clearAllCatalogCaches } from '../../../lib/catalog';
import { RootState } from '../../../store';

interface User {
  id: string;
  email: string;
  role: string;
  firstName?: string;
  first_name?: string;
  tenantId?: string;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  role: string;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const reduxUser = useSelector((state: RootState) => state.auth.user);
  const reduxToken = useSelector((state: RootState) => state.auth.token);
  const [isLoading, setIsLoading] = useState(true);

  // Determine role with fallback logic (matches current Layout.tsx)
  const role = React.useMemo(() => {
    let determinedRole = (reduxUser as any)?.role;

    // Fallback to localStorage if Redux doesn't have user
    if (!determinedRole && typeof window !== 'undefined') {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
        determinedRole = (storedUser as any)?.role || 'customer';
      } catch {
        // Failed to parse stored user, use default role
        determinedRole = 'customer';
      }
    }

    return determinedRole || 'customer';
  }, [reduxUser]);

  // Update loading state when auth state stabilizes
  useEffect(() => {
    // Small delay to allow Redux state to initialize
    const timer = setTimeout(() => setIsLoading(false), 100);
    return () => clearTimeout(timer);
  }, [reduxUser, reduxToken]);

  // Clear catalog cache when auth state changes
  useEffect(() => {
    if (!isLoading) {
      // Auth state has stabilized, clear catalog caches
      clearAllCatalogCaches();
    }
  }, [reduxUser, reduxToken, isLoading]);

  const value: AuthContextType = {
    user: reduxUser,
    role,
    isAuthenticated: !!reduxToken && !!reduxUser,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
